import { createContext, useContext, useEffect, useState, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';
import { publicClient } from '../config/chain';
import { ChainClockEstimator } from '../lib/chainClock';
import { debugSet } from '../lib/debug';

interface BlockInfo {
  number: bigint;
  timestamp: number;
}

/** Latest block + chain-time estimator shared by the whole app (one block poll per second). */
class ClockStore {
  readonly estimator = new ChainClockEstimator();
  block: BlockInfo | null = null;
  error = false;
  private listeners = new Set<() => void>();

  subscribe = (l: () => void) => {
    this.listeners.add(l);
    return () => {
      this.listeners.delete(l);
    };
  };

  onBlock(number: bigint, timestamp: number) {
    this.error = false;
    if (!this.estimator.observe(number, timestamp, Date.now())) return;
    this.block = { number, timestamp };
    for (const l of this.listeners) l();
  }

  onError() {
    if (this.error) return;
    this.error = true;
    for (const l of this.listeners) l();
  }

  now = (): number | null => this.estimator.now();
}

const ClockContext = createContext<ClockStore | null>(null);

export function ChainClockProvider({ children }: { children: ReactNode }) {
  const [store] = useState(() => new ClockStore());
  useEffect(() => {
    debugSet('clock', {
      now: () => store.now(),
      get rate() {
        return store.estimator.rate;
      },
      get blockNumber() {
        return store.block?.number;
      },
      get blockTimestamp() {
        return store.block?.timestamp;
      },
    });
    return publicClient.watchBlocks({
      emitOnBegin: true,
      pollingInterval: 1_000,
      onBlock: (b) => store.onBlock(b.number, Number(b.timestamp)),
      onError: () => store.onError(),
    });
  }, [store]);
  return <ClockContext.Provider value={store}>{children}</ClockContext.Provider>;
}

function useStore(): ClockStore {
  const s = useContext(ClockContext);
  if (!s) throw new Error('ChainClockProvider missing');
  return s;
}

export interface ChainClock {
  /** estimated current chain timestamp (seconds, fractional) or null before the first block */
  now: () => number | null;
  rate: number;
  block: BlockInfo | null;
  rpcError: boolean;
}

/** Re-renders on every new block. */
export function useChainClock(): ChainClock {
  const store = useStore();
  const block = useSyncExternalStore(store.subscribe, () => store.block);
  const rpcError = useSyncExternalStore(store.subscribe, () => store.error);
  return { now: store.now, rate: store.estimator.rate, block, rpcError };
}

export function useBlockNumber(): bigint | undefined {
  const store = useStore();
  return useSyncExternalStore(store.subscribe, () => store.block?.number);
}

/** Estimated chain time, re-rendering every `intervalMs`. */
export function useChainNow(intervalMs = 500): number | null {
  const store = useStore();
  const [now, setNow] = useState(() => store.now());
  useEffect(() => {
    const tick = () => setNow(store.now());
    tick();
    const id = window.setInterval(tick, intervalMs);
    return () => window.clearInterval(id);
  }, [store, intervalMs]);
  return now;
}

/** Non-reactive accessor for code that samples chain time on demand (miner loop, tx deadlines). */
export function useChainNowGetter(): () => number | null {
  return useStore().now;
}
